"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { OpeningsJson } from "@/lib/api/openingsClient";
import { getDeviceId } from "@/lib/deviceId";

type PhotoLikeSeed = {
  _id: string;
  likeCount?: number;
  liked?: boolean;
};

/**
 * This device's own like state for one photo, held ahead of (or after) the
 * payload that the gallery is currently rendering. Written ONLY by
 * `toggleLike` — optimistically first, then again with the count Openings
 * returns.
 *
 * `seed` is the payload object the override was written against, and it is
 * what keeps an override from outliving its usefulness. See `resolve`.
 */
interface LikeOverride {
  liked: boolean;
  likeCount: number;
  seed: PhotoLikeSeed | undefined;
}

interface ResolvedLike {
  liked: boolean;
  likeCount: number;
}

export interface UsePhotoLikesResult {
  /** Whether like UI is ready (device id resolved on the client). */
  ready: boolean;
  getLikeCount: (photoId: string) => number;
  isLiked: (photoId: string) => boolean;
  isPending: (photoId: string) => boolean;
  toggleLike: (photoId: string) => void;
}

/**
 * Client-side state for anonymous, device-scoped photo likes backed by the
 * Openings photo likes API.
 *
 * **Counts are DERIVED AT RENDER from the `photos` payload, never copied into
 * state by an effect.** An earlier version seeded a `counts` map inside a
 * `useEffect` and guarded it with a `seen` ref, which broke two ways:
 *
 * 1. Effects do not run during server rendering, so every count was `0` in the
 *    server-rendered HTML. Verified against production: `/photos` shipped 24
 *    like buttons and zero like counts, even though the page had already
 *    fetched all 24 counts server-side. `PhotoGrid` only renders the count
 *    when it is `> 0`, so every number popped in a tick after hydration.
 * 2. The `seen` guard skipped any photo it had already recorded, so a
 *    `cache: "no-store"` refetch (every category/business tab switch) had its
 *    fresh counts thrown away. `PhotoGallery` never remounts on a tab change,
 *    so a count stayed frozen at its first-seen value for the whole session.
 *
 * Deriving from props fixes both at once, and — because the server and the
 * client's first render read the same prop — it does so without a hydration
 * mismatch. The per-device `liked` flag still resolves after hydration; that
 * is unavoidable for state keyed on a `localStorage` id the server cannot see.
 */
export const usePhotoLikes = (
  photos: PhotoLikeSeed[],
  business: string,
): UsePhotoLikesResult => {
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<Record<string, LikeOverride>>({});
  // Keyed by the business it was fetched for, so switching tabs retires the
  // previous business's likes by COMPARISON rather than by clearing state —
  // no extra render, and no window where the wrong hearts are filled.
  const [hydratedLikes, setHydratedLikes] = useState<{
    business: string;
    ids: ReadonlySet<string>;
  } | null>(null);
  const [pending, setPending] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );

  const seeds = useMemo(() => {
    const map = new Map<string, PhotoLikeSeed>();
    for (const photo of photos) map.set(photo._id, photo);
    return map;
  }, [photos]);

  // Latest payload, readable from an in-flight request's callbacks. A toggle
  // that started against an older payload must reconcile against whatever is
  // on screen when it lands, not the payload it was fired from.
  const seedsRef = useRef(seeds);
  useEffect(() => {
    seedsRef.current = seeds;
  }, [seeds]);

  // Resolve the device id once on the client.
  useEffect(() => {
    setDeviceId(getDeviceId());
  }, []);

  // Hydrate the device's liked photo ids for the current business. The result
  // is stored WITH the business it belongs to; an earlier version merged every
  // response into one growing set, which leaked Eddie's Edition likes into the
  // Edition Studio tab (and back) as filled hearts on photos never liked there.
  useEffect(() => {
    if (!deviceId) return;
    let cancelled = false;
    const qs = new URLSearchParams({ deviceId, business });
    fetch(`/api/photos/likes?${qs.toString()}`, { cache: "no-store" })
      .then((res) => (res.ok ? res.json() : { likedPhotoIds: [] }))
      .then((json: { likedPhotoIds?: string[] }) => {
        if (cancelled) return;
        setHydratedLikes({
          business,
          ids: new Set(
            Array.isArray(json.likedPhotoIds) ? json.likedPhotoIds : [],
          ),
        });
      })
      .catch(() => {
        /* hydration is best-effort */
      });
    return () => {
      cancelled = true;
    };
  }, [deviceId, business]);

  /**
   * An override wins only while it is still the freshest thing we know: while
   * its request is in flight (a payload that predates this device's like must
   * never undo it on screen), or until a genuinely newer payload supersedes
   * it. "Newer" is object identity — a refetch parses new objects, whereas
   * `loadMore` appends and leaves the existing photo objects untouched, so
   * scrolling never discards a like the visitor just made.
   */
  const resolve = useCallback(
    (photoId: string): ResolvedLike => {
      const seed = seeds.get(photoId);
      const override = overrides[photoId];
      if (override && (pending.has(photoId) || override.seed === seed)) {
        return { liked: override.liked, likeCount: override.likeCount };
      }
      return {
        liked:
          typeof seed?.liked === "boolean"
            ? seed.liked
            : (hydratedLikes?.business === business &&
              hydratedLikes.ids.has(photoId)),
        likeCount: seed?.likeCount ?? 0,
      };
    },
    [seeds, overrides, pending, hydratedLikes, business],
  );

  const getLikeCount = useCallback(
    (photoId: string) => resolve(photoId).likeCount,
    [resolve],
  );
  const isLiked = useCallback(
    (photoId: string) => resolve(photoId).liked,
    [resolve],
  );
  const isPending = useCallback(
    (photoId: string) => pending.has(photoId),
    [pending],
  );

  const toggleLike = useCallback(
    (photoId: string) => {
      if (!deviceId) return;
      if (pending.has(photoId)) return;

      const seed = seeds.get(photoId);
      const current = resolve(photoId);
      const wasLiked = current.liked;

      // Optimistic update.
      setPending((prev) => new Set(prev).add(photoId));
      setOverrides((prev) => ({
        ...prev,
        [photoId]: {
          liked: !wasLiked,
          likeCount: Math.max(0, current.likeCount + (wasLiked ? -1 : 1)),
          seed,
        },
      }));

      const request = wasLiked
        ? OpeningsJson.unlikePhoto({ photoId, deviceId })
        : OpeningsJson.likePhoto({ photoId, deviceId });

      request
        .then((result) => {
          // Reconcile with the server's authoritative count + liked state,
          // stamped against the payload showing NOW — a refetch that landed
          // while this request was in flight is older news than this result.
          setOverrides((prev) => ({
            ...prev,
            [photoId]: {
              liked: result.liked,
              likeCount: result.likeCount,
              seed: seedsRef.current.get(photoId),
            },
          }));
        })
        .catch(() => {
          // Drop the optimistic change and fall back to the payload.
          setOverrides((prev) => {
            if (!(photoId in prev)) return prev;
            const next = { ...prev };
            delete next[photoId];
            return next;
          });
        })
        .finally(() => {
          setPending((prev) => {
            const next = new Set(prev);
            next.delete(photoId);
            return next;
          });
        });
    },
    [deviceId, pending, seeds, resolve],
  );

  return {
    ready: Boolean(deviceId),
    getLikeCount,
    isLiked,
    isPending,
    toggleLike,
  };
};
